import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Phase 2.1: Premium Card Variants with glassmorphism and gradient borders
const cardVariants = cva(
  "relative overflow-hidden rounded-card border transition-all duration-200",
  {
    variants: {
      variant: {
        default: [
          "bg-card text-card-foreground",
          "shadow-card hover:shadow-card-hover",
          "border-border",
        ],
        elevated: [
          "bg-card text-card-foreground",
          "shadow-level-2 hover:shadow-level-3",
          "border-border",
          "hover:-translate-y-1.5",
        ],
        glassmorphic: [
          "bg-background-elevated/95 dark:bg-background-elevated/90",
          "backdrop-blur-md text-card-foreground",
          "border-border/20 dark:border-border/10",
          "shadow-level-2 hover:shadow-level-3",
          "hover:-translate-y-1",
          "before:absolute before:inset-0 before:bg-gradient-glassmorphic before:opacity-50",
          "before:pointer-events-none",
        ],
        gradient: [
          "bg-card text-card-foreground",
          "border-transparent",
          "shadow-level-2 hover:shadow-level-3",
          "hover:-translate-y-1",
          "before:absolute before:inset-0 before:-z-10 before:p-[1px] before:rounded-card",
          "before:bg-gradient-primary before:opacity-30",
          "after:absolute after:inset-[1px] after:rounded-card after:bg-card after:-z-10",
        ],
        premium: [
          "bg-card text-card-foreground",
          "border-border/50",
          "shadow-level-2 hover:shadow-level-4",
          "hover:-translate-y-2 hover:scale-[1.01]",
          "before:absolute before:inset-x-0 before:top-0 before:h-[3px] before:rounded-t-card",
          "before:bg-gradient-primary",
        ],
        interactive: [
          "bg-card text-card-foreground",
          "border-border hover:border-primary/50",
          "shadow-card hover:shadow-level-3",
          "hover:-translate-y-1 hover:scale-[1.02]",
          "cursor-pointer active:scale-[0.98] active:translate-y-0",
        ],
      },
      glow: {
        none: "",
        success: "hover:shadow-glow-success",
        warning: "hover:shadow-glow-warning",
        danger: "hover:shadow-glow-danger",
        info: "hover:shadow-glow-info",
      },
    },
    defaultVariants: {
      variant: "default",
      glow: "none",
    },
  }
)

export interface CardProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof cardVariants> {}

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant, glow, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(cardVariants({ variant, glow }), className)}
      {...props}
    />
  )
)
Card.displayName = "Card"

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col space-y-1.5 p-4 md:p-6", className)}
    {...props}
  />
))
CardHeader.displayName = "CardHeader"

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      "text-h3 font-semibold leading-tight tracking-tight",
      className
    )}
    {...props}
  />
))
CardTitle.displayName = "CardTitle"

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn("text-body-small text-muted-foreground", className)}
    {...props}
  />
))
CardDescription.displayName = "CardDescription"

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("p-4 pt-0 md:p-6", className)} {...props} />
))
CardContent.displayName = "CardContent"

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex items-center p-4 pt-0 md:p-6 gap-2 flex-wrap", className)}
    {...props}
  />
))
CardFooter.displayName = "CardFooter"

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
